import { useCallback, useEffect, useState } from 'react'

import { F0Button, F0Dialog, F0Select } from '@factorialco/f0-react'
import { AutoGrid } from '@factorialco/f0-react/dist/experimental'
import { Megaphone, Phone, Settings } from '@factorialco/f0-react/icons/app'
import {
  useLocalParticipant,
  useMediaDeviceSelect,
  usePersistentUserChoices,
} from '@livekit/components-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

const isAudioOutputSelectionSupported = 'setSinkId' in HTMLMediaElement.prototype

export const SettingsButton = () => {
  const [openModal, setOpenModal] = useState(false)
  const { saveAudioInputDeviceId } = usePersistentUserChoices()
  const { localParticipant } = useLocalParticipant()

  const {
    devices: audioInputDevices,
    activeDeviceId: activeAudioInputId,
    setActiveMediaDevice: setActiveAudioInputDevice,
  } = useMediaDeviceSelect({ kind: 'audioinput' })

  const {
    devices: audioOutputDevices,
    activeDeviceId: activeAudioOutputId,
    setActiveMediaDevice: setActiveAudioOutputDevice,
  } = useMediaDeviceSelect({ kind: 'audiooutput' })

  useEffect(() => {
    const savedAudioOutputDeviceId = localStorage.getItem('livekit-audio-output-device')
    if (savedAudioOutputDeviceId && setActiveAudioOutputDevice) {
      setActiveAudioOutputDevice(savedAudioOutputDeviceId)
    }
  }, [setActiveAudioOutputDevice])

  const handleAudioInputChange = useCallback(
    async (deviceId: string) => {
      try {
        if (setActiveAudioInputDevice) {
          await setActiveAudioInputDevice(deviceId)
          if (!localParticipant.isMicrophoneEnabled) {
            await localParticipant.setMicrophoneEnabled(true)
          }
        }
        saveAudioInputDeviceId(deviceId)
      } catch {}
    },
    [localParticipant, saveAudioInputDeviceId, setActiveAudioInputDevice]
  )

  const handleAudioOutputChange = useCallback(
    async (deviceId: string) => {
      if (isAudioOutputSelectionSupported && setActiveAudioOutputDevice) {
        await setActiveAudioOutputDevice(deviceId)
        localStorage.setItem('livekit-audio-output-device', deviceId)
      }
    },
    [setActiveAudioOutputDevice]
  )

  return (
    <>
      <F0Button
        label={i18n.t('common.ai.voice.control_bar.settings')}
        onClick={() => {
          track(trackingEvents.meetings.ControlBarSettingsButton)
          setOpenModal(true)
        }}
        icon={Settings}
        hideLabel
        variant="outline"
        size="md"
      />

      <F0Dialog
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={i18n.t('common.ai.voice.settings_modal.title')}
        width="sm"
      >
        <AutoGrid gap="4" tileSize="sm">
          <F0Select
            onChange={(deviceId: string) => {
              track(trackingEvents.meetings.SettingsModalAudioInputChange, {
                deviceId,
              })
              handleAudioInputChange(deviceId)
            }}
            options={audioInputDevices.map((device) => ({
              label:
                device.label ||
                `${i18n.t(
                  'common.ai.voice.control_bar.microphone.label'
                )} ${device.deviceId.substring(0, 5)}...`,
              value: device.deviceId,
            }))}
            placeholder={i18n.t('common.ai.voice.control_bar.microphone.placeholder')}
            searchBoxPlaceholder={i18n.t(
              'common.ai.voice.control_bar.microphone.search_placeholder'
            )}
            searchEmptyMessage={i18n.t(
              'common.ai.voice.control_bar.microphone.no_microphones_found'
            )}
            value={activeAudioInputId}
            icon={Megaphone}
            label={i18n.t('common.ai.voice.settings_modal.audio_input')}
          />

          {isAudioOutputSelectionSupported && (
            <F0Select
              onChange={(deviceId: string) => {
                track(trackingEvents.meetings.SettingsModalAudioOutputChange, {
                  deviceId,
                })
                handleAudioOutputChange(deviceId)
              }}
              options={audioOutputDevices.map((device) => ({
                label:
                  device.label ||
                  `${i18n.t(
                    'common.ai.voice.control_bar.speakers.label'
                  )} ${device.deviceId.substring(0, 5)}...`,
                value: device.deviceId,
              }))}
              placeholder={i18n.t('common.ai.voice.control_bar.speakers.placeholder')}
              searchBoxPlaceholder={i18n.t(
                'common.ai.voice.control_bar.speakers.search_placeholder'
              )}
              searchEmptyMessage={i18n.t('common.ai.voice.control_bar.speakers.no_speakers_found')}
              value={activeAudioOutputId}
              icon={Phone}
              label={i18n.t('common.ai.voice.settings_modal.audio_output')}
            />
          )}
        </AutoGrid>
      </F0Dialog>
    </>
  )
}
